import { MapPin, ChevronRight, ArrowRight, Phone } from "lucide-react";

/*
 * AreasGrid — Section 2/8 for /service-areas/
 * Cream section — 12 location routing cards with suburb counts & SEO descriptions.
 * Each card links to a future location page (will 404 until built).
 * Paste this ENTIRE file into src/pages/ServiceAreas/sections/AreasGrid.tsx
 * replacing the placeholder content.
 */

const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM2 = "#F0EBE0";
const INK = "#1A1F2E";
const INK2 = "#5A6478";
const NAVY = "#081438";
const AREAS = [{
  name: "Eastern Suburbs",
  href: "/garage-door-repair-eastern-suburbs/",
  suburbs: "Bondi · Rose Bay · Vaucluse · Woollahra · Randwick · Coogee",
  count: "20+",
  desc: "Same-day garage door repairs across the Eastern Suburbs — from Bondi Beach to Little Bay.",
  featured: true
}, {
  name: "Western Sydney",
  href: "/garage-door-repair-western-sydney/",
  suburbs: "Parramatta · Blacktown · Penrith · Liverpool · Fairfield",
  count: "35+",
  desc: "Sydney's largest service region. Fast response from our Bass Hill base to all of Western Sydney.",
  featured: true
}, {
  name: "North Shore",
  href: "/garage-door-repair-north-shore/",
  suburbs: "Chatswood · Lane Cove · Mosman · Willoughby · Neutral Bay",
  count: "20+",
  desc: "Premium garage door service across the Lower and Upper North Shore.",
  featured: true
}, {
  name: "Inner West",
  href: "/garage-door-repair-inner-west/",
  suburbs: "Marrickville · Newtown · Balmain · Leichhardt · Ashfield",
  count: "15+",
  desc: "Terrace house & tight-access garage door specialists for the Inner West.",
  featured: false
}, {
  name: "Northern Beaches",
  href: "/garage-door-repair-northern-beaches/",
  suburbs: "Manly · Dee Why · Brookvale · Mona Vale · Avalon",
  count: "15+",
  desc: "Coastal garage door repairs — salt-air corrosion fixes and roller door servicing.",
  featured: false
}, {
  name: "Hills District",
  href: "/garage-door-repair-hills-district/",
  suburbs: "Castle Hill · Baulkham Hills · Kellyville · Rouse Hill · Dural",
  count: "15+",
  desc: "New builds and upgrades across The Hills. Sectional and panel-lift specialists.",
  featured: false
}, {
  name: "Sutherland Shire",
  href: "/garage-door-repair-sutherland-shire/",
  suburbs: "Cronulla · Miranda · Caringbah · Engadine · Sylvania",
  count: "15+",
  desc: "From Cronulla to Engadine — springs, motors and full door replacement.",
  featured: false
}, {
  name: "South Sydney",
  href: "/garage-door-repair-south-sydney/",
  suburbs: "Mascot · Rockdale · Kogarah · Hurstville · Sans Souci",
  count: "15+",
  desc: "Quick-dispatch repairs across St George and Bayside — close to our home base.",
  featured: false
}, {
  name: "Canterbury-Bankstown",
  href: "/garage-door-repair-canterbury-bankstown/",
  suburbs: "Bankstown · Canterbury · Lakemba · Punchbowl · Bass Hill",
  count: "15+",
  desc: "Our home turf. Fastest response times — often under 45 minutes from call to arrival.",
  featured: false
}, {
  name: "Wollongong & Illawarra",
  href: "/garage-door-repair-wollongong/",
  suburbs: "Wollongong · Shellharbour · Kiama · Dapto · Corrimal",
  count: "15+",
  desc: "Dedicated Illawarra service days — garage door repairs from Helensburgh to Kiama.",
  featured: true
}, {
  name: "Central Coast",
  href: "/garage-door-repair-central-coast/",
  suburbs: "Gosford · Terrigal · Erina · Tuggerah · Woy Woy",
  count: "15+",
  desc: "Scheduled Central Coast service — same quality, same warranty, every visit.",
  featured: true
}, {
  name: "Blue Mountains",
  href: "/garage-door-repair-blue-mountains/",
  suburbs: "Katoomba · Leura · Springwood · Blaxland · Penrith fringe",
  count: "10+",
  desc: "Mountain-climate specialists — insulated doors, weatherproofing and cold-rated springs.",
  featured: false
}];
export default function AreasGrid() {
  const featuredAreas = AREAS.filter(a => a.featured);
  const otherAreas = AREAS.filter(a => !a.featured);
  return <section id="areas-grid" style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden"
  }}>

      {/* Wave top — DUPLICATE from AreasHero bottom */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,0 L0,0 Z" fill={NAVY} />
        </svg>
      </div>

      {/* Paper grain */}
      <svg aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.04,
      pointerEvents: "none",
      zIndex: 1
    }}>
        <filter id="ag-grain"><feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="4" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
        <rect width="100%" height="100%" filter="url(#ag-grain)" />
      </svg>

      {/* Corner ornaments */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: "40px",
      left: "40px",
      width: "64px",
      height: "64px",
      borderTop: `2px solid ${GOLD}50`,
      borderLeft: `2px solid ${GOLD}50`,
      zIndex: 1
    }} />
      <div aria-hidden="true" style={{
      position: "absolute",
      bottom: "40px",
      right: "40px",
      width: "64px",
      height: "64px",
      borderBottom: `2px solid ${GOLD}50`,
      borderRight: `2px solid ${GOLD}50`,
      zIndex: 1
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "clamp(56px,7vw,88px) clamp(20px,5vw,48px)"
    }}>

        {/* Section header */}
        <div style={{
        maxWidth: "640px",
        marginBottom: "48px"
      }}>
          <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "20px"
        }}>
            <div style={{
            width: "32px",
            height: "2px",
            background: GOLD
          }} />
            <span style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              Find Your Area
            </span>
          </div>
          <h2 style={{
          fontFamily: "Plus Jakarta Sans,sans-serif",
          fontWeight: 800,
          fontSize: "clamp(26px,3.5vw,42px)",
          lineHeight: 1.1,
          color: INK,
          marginBottom: "14px"
        }}>
            12 Regions. <span style={{
            color: GOLD_DARK
          }}>One Call.</span>
          </h2>
          <p style={{
          fontFamily: "Manrope,sans-serif",
          fontSize: "clamp(14px,1.5vw,16px)",
          color: INK2,
          lineHeight: 1.7
        }}>
            Select your region below. Every area has a dedicated page with local suburb coverage, services available and direct booking.
          </p>
        </div>

        {/* ── Featured areas — larger cards ── */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
        gap: "20px",
        marginBottom: "20px"
      }}>
          {featuredAreas.map(area => <a key={area.name} href={area.href} style={{
          display: "block",
          background: "#FFFFFF",
          border: `1.5px solid ${GOLD}30`,
          borderRadius: "14px",
          padding: "28px",
          textDecoration: "none",
          position: "relative",
          transition: "border-color 0.2s,box-shadow 0.2s",
          boxShadow: "0 2px 12px rgba(0,0,0,0.05)"
        }} className="hover:!border-[#F5A623] hover:!shadow-lg">
              {/* Featured badge */}
              <div style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: `${GOLD}15`,
            border: `1px solid ${GOLD}35`,
            borderRadius: "999px",
            padding: "3px 10px"
          }}>
                <span style={{
              fontFamily: "Manrope,sans-serif",
              fontSize: "10px",
              fontWeight: 700,
              color: GOLD_DARK,
              letterSpacing: "0.05em",
              textTransform: "uppercase"
            }}>Popular</span>
              </div>

              {/* Icon + count */}
              <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px"
          }}>
                {/** @preserve lucide-do-not-replace */}
                <div data-icon-container="lucide-do-not-replace" style={{
              width: "44px",
              height: "44px",
              borderRadius: "10px",
              background: `${GOLD}12`,
              border: `1px solid ${GOLD}25`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
                  <MapPin size={22} color={GOLD_DARK} strokeWidth={1.8} />
                </div>
                <div>
                  <h3 style={{
                fontFamily: "Plus Jakarta Sans,sans-serif",
                fontWeight: 700,
                fontSize: "18px",
                color: INK,
                lineHeight: 1.2,
                marginBottom: "2px"
              }}>
                    {area.name}
                  </h3>
                  <span style={{
                fontFamily: "Manrope,sans-serif",
                fontSize: "12px",
                color: GOLD_DARK,
                fontWeight: 600
              }}>
                    {area.count} suburbs
                  </span>
                </div>
              </div>

              <p style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "13px",
            color: INK2,
            lineHeight: 1.65,
            marginBottom: "12px"
          }}>
                {area.desc}
              </p>

              <p style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "11px",
            color: `${INK2}99`,
            lineHeight: 1.5,
            marginBottom: "16px"
          }}>
                {area.suburbs}
              </p>

              <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            color: GOLD_DARK,
            fontFamily: "Manrope,sans-serif",
            fontSize: "13px",
            fontWeight: 700
          }}>
                View {area.name} services
                {/** @preserve lucide-do-not-replace */}
                <span data-icon-container="lucide-do-not-replace"><ChevronRight size={14} color={GOLD_DARK} strokeWidth={2.5} /></span>
              </div>
            </a>)}
        </div>

        {/* ── Other areas — compact cards ── */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
        gap: "16px"
      }}>
          {otherAreas.map(area => <a key={area.name} href={area.href} style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          background: "#FFFFFF",
          border: `1px solid ${CREAM2}`,
          borderRadius: "10px",
          padding: "18px 20px",
          textDecoration: "none",
          transition: "border-color 0.2s",
          boxShadow: "0 1px 6px rgba(0,0,0,0.04)"
        }} className="hover:!border-[#F5A623]/60">
              {/** @preserve lucide-do-not-replace */}
              <div data-icon-container="lucide-do-not-replace" style={{
            width: "38px",
            height: "38px",
            borderRadius: "8px",
            background: `${GOLD}10`,
            border: `1px solid ${GOLD}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
                <MapPin size={18} color={GOLD_DARK} strokeWidth={1.8} />
              </div>

              <div style={{
            flex: 1,
            minWidth: 0
          }}>
                <h3 style={{
              fontFamily: "Plus Jakarta Sans,sans-serif",
              fontWeight: 700,
              fontSize: "15px",
              color: INK,
              lineHeight: 1.2,
              marginBottom: "2px"
            }}>
                  {area.name}
                </h3>
                <span style={{
              fontFamily: "Manrope,sans-serif",
              fontSize: "11px",
              color: INK2
            }}>
                  {area.count} suburbs · {area.suburbs.split("·")[0].trim()} &amp; more
                </span>
              </div>

              {/** @preserve lucide-do-not-replace */}
              <span data-icon-container="lucide-do-not-replace" style={{
            flexShrink: 0
          }}>
                <ArrowRight size={16} color={GOLD_DARK} strokeWidth={2} />
              </span>
            </a>)}
        </div>

        {/* Bottom CTA */}
        <div style={{
        marginTop: "40px",
        textAlign: "center"
      }}>
          <p style={{
          fontFamily: "Manrope,sans-serif",
          fontSize: "14px",
          color: INK2,
          marginBottom: "16px"
        }}>
            Not sure which area you're in? No worries — we'll sort it.
          </p>
          <a href="tel:0485000933" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          background: NAVY,
          color: "#FFF",
          padding: "14px 28px",
          borderRadius: "8px",
          fontFamily: "Manrope,sans-serif",
          fontWeight: 700,
          fontSize: "15px",
          textDecoration: "none"
        }}>
            {/** @preserve lucide-do-not-replace */}
            <span data-icon-container="lucide-do-not-replace"><Phone size={16} color={GOLD} strokeWidth={2.5} /></span>
            Call 0485 000 933
          </a>
        </div>
      </div>

      {/* Wave bottom — DUPLICATE in AreasSuburbSearch top */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill={NAVY} />
        </svg>
      </div>
    </section>;
}